import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Breadcrumb, Card, Container } from "react-bootstrap";
import SEO from "../components/SEO";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import "../assets/css/Shop.css";
import { PiCrownSimpleDuotone, PiCoinsDuotone } from "react-icons/pi";
import { FiEye } from "react-icons/fi";
import { Paginator } from "primereact/paginator";
import { useNavigate, useNavigationType, useLocation } from "react-router-dom";
import { RiHeart2Line, RiHeart2Fill } from "react-icons/ri";
import { useAuth } from "../context/AuthContext";
import api from "../api/axios";
import useGuestExploreGuard from "../hooks/useGuestExploreGuard";
import GuestExploreOverlay from "../components/GuestExploreOverlay";
import { useDebounce } from "use-debounce";
import { Skeleton } from "primereact/skeleton";
import { toast } from "react-toastify";
import { slugify } from "../utils/slugify";
const SHOP_STATE_KEY = "shop_state_v1";

const SHOP_DEFAULT_STATE = {
  category: { category_name: "", category_id: 0 },
  subcategory: null,
  searchTerm: "",
  sortBy: "featured",
  first: 0,
  rows: 21,
  occasionId: null,
};

const safeParseShopState = () => {
  try {
    return JSON.parse(localStorage.getItem(SHOP_STATE_KEY)) || {};
  } catch {
    return {};
  }
};

const saveShopState = (nextState) => {
  localStorage.setItem(SHOP_STATE_KEY, JSON.stringify(nextState));
  window.dispatchEvent(
    new CustomEvent("shop-state-updated", {
      detail: nextState,
    })
  );
};


const ProtectedCanvasImage = ({
  src,
  alt = "",
  onClick,
  onLoad,
  onError,
  height = 220,
  objectFit = "cover",
  objectPosition = "top center",
  className = "",
  style = {},
}) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);

  const drawImageToCanvas = () => {
    const canvas = canvasRef.current;
    const image = imageRef.current;

    if (!canvas || !image) return;

    const rect = canvas.getBoundingClientRect();

    const displayWidth = Math.max(
      rect.width || canvas.clientWidth || 1,
      1
    );

    const fallbackHeight =
      typeof height === "number"
        ? height
        : parseFloat(height) || 220;

    const displayHeight = Math.max(
      rect.height || canvas.clientHeight || fallbackHeight,
      1
    );

    const pixelRatio = Math.min(
      window.devicePixelRatio || 1,
      2
    );

    canvas.width = Math.round(
      displayWidth * pixelRatio
    );

    canvas.height = Math.round(
      displayHeight * pixelRatio
    );

    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    ctx.setTransform(
      pixelRatio,
      0,
      0,
      pixelRatio,
      0,
      0
    );

    ctx.clearRect(
      0,
      0,
      displayWidth,
      displayHeight
    );

    const sourceWidth = image.naturalWidth;
    const sourceHeight = image.naturalHeight;

    if (!sourceWidth || !sourceHeight) {
      return;
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    if (objectFit === "contain") {
      const scale = Math.min(
        displayWidth / sourceWidth,
        displayHeight / sourceHeight
      );

      const drawWidth =
        sourceWidth * scale;

      const drawHeight =
        sourceHeight * scale;

      const dx =
        (displayWidth - drawWidth) / 2;

      const isTop =
        String(objectPosition)
          .toLowerCase()
          .includes("top");

      const dy =
        isTop
          ? 0
          : (displayHeight - drawHeight) / 2;

      ctx.drawImage(
        image,
        dx,
        dy,
        drawWidth,
        drawHeight
      );

      return;
    }

    /*
     * object-fit: cover
     * object-position: top center
     */
    const sourceAspect =
      sourceWidth / sourceHeight;

    const targetAspect =
      displayWidth / displayHeight;

    let sx = 0;
    let sy = 0;
    let sw = sourceWidth;
    let sh = sourceHeight;

    if (sourceAspect > targetAspect) {
      sw =
        sourceHeight *
        targetAspect;

      sx =
        (sourceWidth - sw) / 2;
    } else {
      sh =
        sourceWidth /
        targetAspect;

      const isTop =
        String(objectPosition)
          .toLowerCase()
          .includes("top");

      sy =
        isTop
          ? 0
          : (sourceHeight - sh) / 2;
    }

    ctx.drawImage(
      image,
      sx,
      sy,
      sw,
      sh,
      0,
      0,
      displayWidth,
      displayHeight
    );
  };

  useEffect(() => {
    if (!src) {
      onError?.();
      return;
    }

    let cancelled = false;
    let resizeObserver = null;

    /*
     * IMPORTANT:
     *
     * Do NOT use fetch(src).blob() here.
     *
     * A normal <img> can display a CDN image even when that CDN
     * does not allow browser fetch/XHR through CORS.
     *
     * fetch() was therefore failing for your thumbnails.
     *
     * Loading with new Image() allows the browser to display the
     * cross-origin image and draw it to canvas.
     *
     * We intentionally do NOT set image.crossOrigin. For a
     * cross-origin CDN this also leaves the canvas "tainted",
     * meaning browser scripts cannot export it using toDataURL()
     * or getImageData().
     */
    const image = new Image();
    image.decoding = "async";

    image.onload = () => {
      if (cancelled) return;

      imageRef.current = image;

      requestAnimationFrame(() => {
        drawImageToCanvas();
        onLoad?.();
      });

      if (
        typeof ResizeObserver !== "undefined" &&
        canvasRef.current
      ) {
        resizeObserver =
          new ResizeObserver(() => {
            requestAnimationFrame(
              drawImageToCanvas
            );
          });

        resizeObserver.observe(
          canvasRef.current
        );
      }
    };

    image.onerror = () => {
      if (cancelled) return;

      console.error(
        "Protected canvas image failed to load:",
        src
      );

      onError?.();
    };

    image.src = src;

    return () => {
      cancelled = true;

      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      image.onload = null;
      image.onerror = null;
      imageRef.current = null;
    };
  }, [
    src,
    height,
    objectFit,
    objectPosition,
  ]);

  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label={alt}
      className={className}
      onClick={onClick}
      onContextMenu={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onDragStart={(event) => {
        event.preventDefault();
      }}
      style={{
        display: "block",
        width: "100%",
        height:
          typeof height === "number"
            ? `${height}px`
            : height,
        cursor:
          onClick
            ? "pointer"
            : "default",
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        ...style,
      }}
    />
  );
};


const Shop = () => {
  const navigate = useNavigate();
  const { isAuthenticated, tierlevel } = useAuth();

  const {
    guestGuardReady,
    verificationFailed,
    captureGuestClick,
    getPageStatus,
  } = useGuestExploreGuard(isAuthenticated, tierlevel);

  const shopGuestStatus = getPageStatus("shop");
  const isShopLocked = shopGuestStatus.locked;

  const location = useLocation();

  const navigationType = useNavigationType();
  const isBackNavigation = navigationType === "POP";

  const savedState = safeParseShopState();

  const incomingCategoryName = location.state?.category ?? "";
  const incomingSubCategoryName = location.state?.subcategory || null;
  const incomingSearchTerm = location.state?.searchTerm;
  const incomingOccasionId =
    location.state?.occasionId ?? location.state?.occasion_id ?? null;
  // A POP means the user returned to an existing Shop history entry. In that
  // case its location state describes how the entry was originally opened; it
  // is not a new filter request. Reapplying it would reset `first` to 0 and
  // lose the page restored from localStorage.
  const hasIncomingState = Boolean(location.state) && !isBackNavigation;
  const shouldResetShop =
    hasIncomingState && location.state?.reset === true;

  const [visibleLimit, setVisibleLimit] = useState(50);
  const [showSidebar, setShowSidebar] = useState(false);

  const [categories, setCategories] = useState([]);
  const [occasionOptions, setOccasionOptions] = useState([]);

  const [category, setCategory] = useState(() => {
    if (shouldResetShop) return SHOP_DEFAULT_STATE.category;
    return savedState.category || null;
  });

  const [subcategories, setSubcategories] = useState([]);

  const [subcategory, setSubcategory] = useState(() => {
    if (shouldResetShop) return null;
    return savedState.subcategory || null;
  });

  const [products, setProducts] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const [searchTerm, setSearchTerm] = useState(() => {
    if (shouldResetShop) return "";
    return savedState.searchTerm || "";
  });

  const [sortBy, setSortBy] = useState(() => {
    if (shouldResetShop) return "featured";
    return savedState.sortBy || "featured";
  });

  const [occasionId, setOccasionId] = useState(() => {
    if (shouldResetShop) return null;
    return savedState.occasionId || null;
  });

  const [first, setFirst] = useState(() => {
    if (shouldResetShop) return 0;
    return savedState.first ?? 0;
  });

  const [rows, setRows] = useState(() => {
    if (shouldResetShop) return 21;
    return savedState.rows ?? 21;
  });

  const [debouncedSearch] = useDebounce(searchTerm, 500);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const isSearchPending = searchTerm !== debouncedSearch;
  const showProductSkeleton = loadingProducts || isSearchPending;

  const [cardLoaded, setCardLoaded] = useState({});
  const [wishlist, setWishlist] = useState({});
  const [showFilter, setShowFilter] = useState(false);

  const productCache = useRef(new Map());
  const categoriesFetched = useRef(false);
  const occasionsFetched = useRef(false);
  const inFlightRequests = useRef(new Map());
  // Only the response for the latest filters may update the grid; older
  // responses (e.g. the previous page before `first` reset) are cached only.
  const latestRequestKey = useRef(null);
  const isRestoringState = useRef(true);

  const currentPage = Math.floor(first / rows) + 1;

  const sortOptions = [
    { label: "Featured", value: "featured" },
    { label: "Alphabetically, A-Z", value: "name_asc" },
    { label: "Alphabetically, Z-A", value: "name_desc" },
    { label: "Price, low to high", value: "price_asc" },
    { label: "Price, high to low", value: "price_desc" },
  ];

  const selectedOccasion =
    occasionOptions.find((item) => String(item.id) === String(occasionId)) ||
    occasionOptions.find((item) => item.id === null) ||
    null;

  const dispatchCurrentState = (stateOverride = {}) => {
    const nextState = {
      category,
      subcategory,
      searchTerm,
      sortBy,
      first,
      rows,
      occasionId,
      ...stateOverride,
    };

    saveShopState(nextState);
  };

  const resetShopState = (allCategory) => {
    const nextState = {
      ...SHOP_DEFAULT_STATE,
      category: allCategory || SHOP_DEFAULT_STATE.category,
    };

    setCategory(nextState.category);
    setSubcategory(null);
    setSearchTerm("");
    setSortBy("featured");
    setOccasionId(null);
    setFirst(0);
    setRows(21);
    setShowFilter(false);
    setShowSidebar(false);
    productCache.current.clear();

    saveShopState(nextState);
  };

  const handleCardLoad = (id) => {
    setCardLoaded((prev) => ({
      ...prev,
      [id]: true,
    }));
  };

  useEffect(() => {
    const stickyEl = document.getElementById("stickyBanner");
    if (!stickyEl) return;

    const onScroll = () => {
      if (window.scrollY > 120) {
        stickyEl.classList.add("sticky-active");
      } else {
        stickyEl.classList.remove("sticky-active");
      }
    };

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!category) return;

    const stateToStore = {
      category,
      subcategory,
      searchTerm,
      sortBy,
      first,
      rows,
      occasionId,
    };

    saveShopState(stateToStore);
  }, [category, subcategory, searchTerm, sortBy, first, rows, occasionId]);

  useEffect(() => {
    if (categoriesFetched.current) return;
    categoriesFetched.current = true;

    const fetchCategories = async () => {
      try {
        const response = await api.get(
          `/CategoryRoutes/customer/categories-with-subcategories`
        );

        if (response.status === 200 && response.data?.success) {
          const allCategoryOption = { category_name: "", category_id: 0 };
          const updatedCategories = [allCategoryOption, ...response.data.data];

          setCategories(updatedCategories);

          if (shouldResetShop) {
            resetShopState(allCategoryOption);
            isRestoringState.current = false;
            return;
          }

          const latestSaved = safeParseShopState();

          const savedCategoryName =
            incomingCategoryName ||
            latestSaved.category?.category_name ||
            "";

          const matchedCategory =
            updatedCategories.find(
              (c) => c.category_name === savedCategoryName
            ) || allCategoryOption;

          setCategory(matchedCategory);

          if (hasIncomingState) {
            setSubcategory(
              incomingSubCategoryName &&
                incomingSubCategoryName.toLowerCase() !== "all"
                ? incomingSubCategoryName
                : null
            );
            setSearchTerm(incomingSearchTerm ?? "");
            setOccasionId(incomingOccasionId ?? null);
            setSortBy(location.state?.sortBy || "featured");
            setFirst(0);

            saveShopState({
              ...SHOP_DEFAULT_STATE,
              category: matchedCategory,
              subcategory:
                incomingSubCategoryName &&
                  incomingSubCategoryName.toLowerCase() !== "all"
                  ? incomingSubCategoryName
                  : null,
              searchTerm: incomingSearchTerm ?? "",
              occasionId: incomingOccasionId ?? null,
              sortBy: location.state?.sortBy || "featured",
              first: 0,
              rows,
            });
          } else {
            setSubcategory(
              latestSaved.subcategory &&
                latestSaved.subcategory.toLowerCase() !== "all"
                ? latestSaved.subcategory
                : null
            );
            setSearchTerm(latestSaved.searchTerm || "");
            setOccasionId(latestSaved.occasionId || null);
            setSortBy(latestSaved.sortBy || "featured");
            setFirst(latestSaved.first ?? 0);
            setRows(latestSaved.rows ?? 21);
          }

          isRestoringState.current = false;
        }
      } catch {
        toast.error("Failed to fetch Categories");
        isRestoringState.current = false;
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (!shouldResetShop || categories.length === 0) return;

    const allCategory =
      categories.find((c) => c.category_name === "") ||
      SHOP_DEFAULT_STATE.category;

    resetShopState(allCategory);
    isRestoringState.current = false;
  }, [shouldResetShop, categories]);

  useEffect(() => {
    if (!hasIncomingState || shouldResetShop || categories.length === 0) return;

    const matched =
      categories.find((c) => c.category_name === incomingCategoryName) ||
      categories.find((c) => c.category_name === "") ||
      SHOP_DEFAULT_STATE.category;

    setCategory(matched);
    setSubcategory(
      incomingSubCategoryName &&
        incomingSubCategoryName.toLowerCase() !== "all"
        ? incomingSubCategoryName
        : null
    );
    setSearchTerm(incomingSearchTerm ?? "");
    setOccasionId(incomingOccasionId ?? null);
    setSortBy(location.state?.sortBy || "featured");
    setFirst(0);

    saveShopState({
      ...SHOP_DEFAULT_STATE,
      category: matched,
      subcategory:
        incomingSubCategoryName &&
          incomingSubCategoryName.toLowerCase() !== "all"
          ? incomingSubCategoryName
          : null,
      searchTerm: incomingSearchTerm ?? "",
      occasionId: incomingOccasionId ?? null,
      sortBy: location.state?.sortBy || "featured",
      first: 0,
      rows,
    });
  }, [
    location.key,
    hasIncomingState,
    shouldResetShop,
    categories,
    incomingCategoryName,
    incomingSubCategoryName,
    incomingSearchTerm,
    incomingOccasionId,
  ]);

  useEffect(() => {
    if (occasionsFetched.current) return;
    occasionsFetched.current = true;

    const fetchOccasions = async () => {
      try {
        const response = await api.get(
          "/OccasionRoutes/public/fetch_occasions_for_filter"
        );

        if (response.status === 200 && response.data?.success) {
          const allOccasionOption = {
            id: null,
            occasion_name: "All",
          };

          setOccasionOptions([
            allOccasionOption,
            ...(response.data.data || []),
          ]);
        } else {
          setOccasionOptions([
            {
              id: null,
              occasion_name: "All",
            },
          ]);
        }
      } catch (error) {
        console.error("Failed to fetch occasions:", error);
        toast.error("Failed to fetch occasions");

        setOccasionOptions([
          {
            id: null,
            occasion_name: "All",
          },
        ]);
      }
    };

    fetchOccasions();
  }, []);

  useEffect(() => {
    if (!category || !category.category_id) {
      setSubcategories([]);
      return;
    }

    const selected = categories.find(
      (c) => c.category_id === category.category_id
    );

    const filteredSubcategories = Array.from(
      new Map(
        (selected?.subcategories || [])
          .filter(
            (sub) =>
              sub?.subcategory_name &&
              sub.subcategory_name.trim().toLowerCase() !== "all"
          )
          .map((sub) => [sub.subcategory_name.trim().toLowerCase(), sub])
      ).values()
    );

    setSubcategories(filteredSubcategories);
  }, [category, categories]);

  useEffect(() => {
    productCache.current.clear();
  }, [rows]);

  useEffect(() => {
    if (isBackNavigation) return;
    if (isRestoringState.current) return;

    setFirst(0);
  }, [
    debouncedSearch,
    category,
    sortBy,
    occasionId,
    subcategory,
    isBackNavigation,
  ]);

  useEffect(() => {
    if (!category) return;

    /*
     * For guests, wait for the server-side identity/click check
     * before loading shop products.
     */
    if (
      !isAuthenticated &&
      (!guestGuardReady || isShopLocked)
    ) {
      return;
    }

    const fetchProducts = async () => {
      const cacheKey = JSON.stringify({
        search: debouncedSearch,
        category: category?.category_name,
        subcategory,
        occasionId,
        page: currentPage,
        rows,
        sortBy,
      });

      latestRequestKey.current = cacheKey;

      if (productCache.current.has(cacheKey)) {
        const cached = productCache.current.get(cacheKey);
        setProducts(cached.products);
        setBundles(cached.bundles || []);
        setTotalRecords(cached.totalRecords);
        setLoadingProducts(false);
        return;
      }

      setLoadingProducts(true);

      if (inFlightRequests.current.has(cacheKey)) {
        return;
      }

      const requestPromise = api
        .get(`/ProductRoutes/customer/products`, {
          params: {
            searchTerm: debouncedSearch,
            category: category?.category_name || "",
            subcategory:
              subcategory && subcategory.toLowerCase() !== "all"
                ? subcategory
                : "",
            occasion_id: occasionId,
            page: currentPage,
            limit: rows,
            sortBy,
          },
        })
        .then((res) => {
          if (res.data?.success) {
            productCache.current.set(cacheKey, {
              products: res.data.data || [],
              bundles: res.data.bundles || [],
              totalRecords: res.data.pagination?.totalRecords || 0,
            });

            if (latestRequestKey.current !== cacheKey) return;

            setProducts(res.data.data || []);
            setBundles(res.data.bundles || []);
            setTotalRecords(res.data.pagination?.totalRecords || 0);
          }
        })
        .catch(() => {
          if (latestRequestKey.current !== cacheKey) return;
          toast.error("Failed to fetch products");
        })
        .finally(() => {
          inFlightRequests.current.delete(cacheKey);
          if (latestRequestKey.current === cacheKey) {
            setLoadingProducts(false);
          }
        });

      inFlightRequests.current.set(cacheKey, requestPromise);
    };

    fetchProducts();
  }, [
    debouncedSearch,
    category,
    currentPage,
    occasionId,
    rows,
    sortBy,
    subcategory,
    isAuthenticated,
    guestGuardReady,
    isShopLocked,
  ]);

  useEffect(() => {
    if (
      !isAuthenticated &&
      isShopLocked
    ) {
      setProducts([]);
      setBundles([]);
      setTotalRecords(0);
      setLoadingProducts(false);
      productCache.current.clear();
    }
  }, [
    isAuthenticated,
    isShopLocked,
  ]);


  const toggleWishlist = async (product_id) => {
    if (!isAuthenticated) {
      toast.warn("Please login to add items to wishlist");
      return;
    }

    try {
      const res = await api.post(`/custwishroutes/wishlist/add`, {
        product_id,
      });

      if (res.status === 201) {
        setWishlist((prev) => ({
          ...prev,
          [product_id]: true,
        }));

        toast.success("Added to wishlist");
      }
    } catch (err) {
      if (err.response?.status === 409) {
        toast.info("Already in wishlist");

        setWishlist((prev) => ({
          ...prev,
          [product_id]: true,
        }));
      } else {
        toast.error("Unable to add to wishlist");
      }
    }
  };

  useEffect(() => {
    const updateLimit = () => {
      if (window.innerWidth <= 991) {
        setVisibleLimit(18);
      } else {
        setVisibleLimit(50);
      }
    };

    updateLimit();
    window.addEventListener("resize", updateLimit);

    return () => window.removeEventListener("resize", updateLimit);
  }, []);

  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const clearSubcategory = () => {
    setSubcategory(null);
    setFirst(0);
    setShowSidebar(false);

    dispatchCurrentState({
      subcategory: null,
      first: 0,
    });
  };

  return (
    <>
      <SEO
        title="Shop"
        description="Browse premium stock images, wallpapers, and digital assets. Discover featured and trending products in our shop."
        canonical="https://shaadimaterial.com/shop"
        keywords="stock images, wallpapers, digital assets, premium images, shop"
        image="https://shaadimaterial.com/logo192.png"
      />

      <div
        onClickCapture={(event) =>
          captureGuestClick(
            event,
            "shop"
          )
        }
      >
        <Container className="py-5 containerfull shopcontainer">
        <Row className="mx-0 breadcrumb_part align-items-center respbreadcrumbpadding">
          <Col md={6} className="px-0">
            <h3 data-aos="fade-up" data-aos-delay="220">Shop</h3>

            <Breadcrumb data-aos="fade-up" data-aos-delay="220">
              <Breadcrumb.Item
                onClick={() => navigate(isAuthenticated ? "/home" : "/")}
              >
                Home
              </Breadcrumb.Item>

              <Breadcrumb.Item active>Shop</Breadcrumb.Item>
            </Breadcrumb>
          </Col>
        </Row>

        <Row className="mx-0 pt-lg-5 pt-2">
          <Col md={12} className="p-0">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
              <div
                data-aos="fade-up"
                data-aos-delay="220"
                className="d-flex align-items-center gap-2 flex-wrap"
              >
                <Button
                  label="Filter"
                  icon={showFilter ? "pi pi-times" : "pi pi-filter"}
                  className="filterbutton py-2"
                  outlined
                  onClick={() => setShowFilter(!showFilter)}
                />

                {subcategory && (
                  <button
                    type="button"
                    className="selected-subcategory-chip"
                    onClick={clearSubcategory}
                  >
                    {subcategory}
                    <span>×</span>
                  </button>
                )}
              </div>

              <div
                className="dropdownpartflt d-flex align-items-center justify-content-end gap-1 flex-wrap"
                data-aos="fade-up"
                data-aos-delay="220"
              >
                <div className="d-flex align-items-center gap-2">
                  <label className="mb-0" style={{ fontSize: "14px" }}>
                    Occasion
                  </label>

                  <Dropdown
                    value={selectedOccasion}
                    options={occasionOptions}
                    optionLabel="occasion_name"
                    placeholder="All"
                    onChange={(e) => {
                      setOccasionId(e.value?.id ?? null);
                      setFirst(0);
                    }}
                    className="occasion-dropdown"
                  />
                </div>

                <div className="d-flex align-items-center gap-2">
                  <label className="mb-0" style={{ fontSize: "14px" }}>
                    Sort by
                  </label>

                  <Dropdown
                    value={sortBy}
                    options={sortOptions}
                    optionLabel="label"
                    onChange={(e) => {
                      setSortBy(e.value);
                      setFirst(0);
                    }}
                  />
                </div>
              </div>
            </div>

            {showFilter && (
              <div className="filterbox">
                <div className="pill-radio-group">
                  <label className={`pill-radio ${!subcategory ? "active" : ""}`}>
                    <input
                      type="radio"
                      checked={!subcategory}
                      onChange={() => {
                        clearSubcategory();
                        setShowFilter(false); // 👈 CLOSE FILTER
                      }}
                    />
                    All
                  </label>

                  {subcategories.slice(0, visibleLimit).map((sub) => (
                    <label
                      key={sub.subcategory_id}
                      className={`pill-radio ${subcategory === sub.subcategory_name ? "active" : ""
                        }`}
                    >
                      <input
                        type="radio"
                        checked={subcategory === sub.subcategory_name}
                        onChange={() => {
                          setSubcategory(sub.subcategory_name);
                          setFirst(0);
                          setShowFilter(false);
                        }}
                      />
                      {sub.subcategory_name}
                    </label>
                  ))}

                  {subcategories.length > visibleLimit && (
                    <button
                      type="button"
                      className="view-more-btn"
                      onClick={() => setShowSidebar(true)}
                    >
                      View More →
                    </button>
                  )}
                </div>
              </div>
            )}

            {showSidebar && (
              <>
                <div
                  className="sidebar-overlay"
                  onClick={() => {
                    setShowSidebar(false);
                    setShowFilter(false);
                  }}
                />

                <div className="filter-sidebar">
                  <div className="sidebar-header">
                    <h5>Filter by Categories</h5>

                    <button
                      type="button"
                      onClick={() => {
                        setShowSidebar(false);
                        setShowFilter(false);
                      }}
                    >
                      ✕
                    </button>
                  </div>

                  <div className="sidebar-body">
                    <div
                      className={`sidebar-item ${!subcategory ? "active" : ""}`}
                      onClick={() => {
                        clearSubcategory();
                        setShowSidebar(false);
                        setShowFilter(false);
                      }}
                    >
                      All
                    </div>

                    {subcategories.map((sub) => (
                      <div
                        key={sub.subcategory_id}
                        className={`sidebar-item ${subcategory === sub.subcategory_name ? "active" : ""
                          }`}
                        onClick={() => {
                          setSubcategory(sub.subcategory_name);
                          setFirst(0);
                          setShowSidebar(false);
                          setShowFilter(false);
                        }}
                      >
                        {sub.subcategory_name}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </Col>

          <Col md={12} className="resppadding">
            <ul
              className="product-listview mb-5"
              data-aos="fade-up"
              data-aos-delay="220"
            >
              {showProductSkeleton ? (
                Array.from({ length: Math.min(rows, 14) }).map((_, index) => (
                  <li key={`skeleton-${index}`}>
                    <Card className="card-click">
                      <Skeleton width="100%" height="220px" className="dark-skeleton" />
                      <div className="product-title">
                        <Skeleton width="80%" height="1rem" className="mb-2 dark-skeleton" />
                        <Skeleton width="40%" height="1rem" className="dark-skeleton" />
                      </div>
                    </Card>
                  </li>
                ))
              ) : products.length === 0 && bundles.length === 0 ? (
                <div className="no-results text-center py-5 w-100">
                  <h5>No products found 😔</h5>
                  <p style={{ color: "#888" }}>
                    Try a different search or filter
                  </p>
                </div>
              ) : (
                [...bundles, ...products].map((p) => {
                  const isBundle =
                    p.item_type === "bundle";

                  const itemKey = isBundle
                    ? `bundle-${p.product_bundle_id}`
                    : p.product_id;

                  const itemName = isBundle
                    ? p.bundle_name
                    : p.product_name;

                  const imageUrl =
                    p.thumbnail?.image_url ||
                    "/placeholder.png";

                  const productUrl = isBundle
                    ? `/bundleproductdetails/${p.product_bundle_id}`
                    : `/product/${p.product_id}/${slugify(
                      p.product_name
                    )}`;

                  const loaded =
                    Boolean(
                      cardLoaded[
                        itemKey
                      ]
                    );

                  return (
                    <li key={itemKey}>
                      <Card className="card-click">

                        <div
                          style={{
                            position:
                              "relative",

                            width:
                              "100%",

                            height:
                              "220px",

                            overflow:
                              "hidden",
                          }}
                        >

                          <div
                            style={{
                              opacity:
                                loaded
                                  ? 1
                                  : 0,

                              transition:
                                "opacity 180ms ease",
                            }}
                          >
                            <ProtectedCanvasImage
                              src={
                                imageUrl
                              }

                              alt={
                                itemName
                              }

                              height={
                                220
                              }

                              onLoad={() =>
                                handleCardLoad(
                                  itemKey
                                )
                              }

                              onError={() =>
                                handleCardLoad(
                                  itemKey
                                )
                              }

                              onClick={() =>
                                navigate(
                                  productUrl
                                )
                              }
                            />
                          </div>


                          {!loaded && (
                            <Skeleton
                              width="100%"
                              height="220px"
                              className="dark-skeleton"

                              style={{
                                position:
                                  "absolute",

                                inset:
                                  0,

                                zIndex:
                                  2,
                              }}
                            />
                          )}

                        </div>


                        {loaded ? (
                          <>

                            {p.is_featured && (
                              <div className="prime_icon">
                                <PiCrownSimpleDuotone />
                              </div>
                            )}

                            {isBundle && (
                              <div className="shop-bundle-badge">
                                Bundle · {p.count_of_assets} assets
                              </div>
                            )}


                            <div className="imageoption">
                              <div className="buttonlist">

                                {isAuthenticated && !isBundle && (
                                  <div
                                    className="view"
                                    title="Wishlist"

                                    onClick={(
                                      e
                                    ) => {
                                      e.stopPropagation();

                                      toggleWishlist(
                                        p.product_id
                                      );
                                    }}
                                  >
                                    {wishlist[
                                      p.product_id
                                    ] ? (
                                      <RiHeart2Fill className="text-warning" />
                                    ) : (
                                      <RiHeart2Line />
                                    )}
                                  </div>
                                )}


                                <div
                                  className="view"
                                  title="View"

                                  onClick={() =>
                                    navigate(
                                      productUrl
                                    )
                                  }
                                >
                                  <FiEye />
                                </div>

                              </div>
                            </div>


                            <div className="product-title position-relative">

                              <p
                                onClick={() =>
                                  navigate(
                                    productUrl
                                  )
                                }
                              >
                                {itemName}
                              </p>


                              <div className="coinpart">

                                <span
                                  className="text-pink iconprice"

                                  style={{
                                    fontSize:
                                      "15px",
                                  }}
                                >
                                  <PiCoinsDuotone />

                                  {" "}

                                  {p.credit_cost ??
                                    "Free"}
                                </span>

                              </div>

                            </div>

                          </>
                        ) : (
                          <div className="product-title">

                            <Skeleton
                              width="80%"
                              height="1rem"
                              className="mb-2 dark-skeleton"
                            />

                            <Skeleton
                              width="40%"
                              height="1rem"
                              className="dark-skeleton"
                            />

                          </div>
                        )}

                      </Card>
                    </li>
                  );
                })
              )}
            </ul>

            <Paginator
              first={first}
              rows={rows}
              className="mb-5 pb-5"
              totalRecords={totalRecords}
              onPageChange={(e) => {
                setFirst(e.first);
                setRows(e.rows);
              }}
              template="PrevPageLink CurrentPageReport NextPageLink"
              currentPageReportTemplate="Page {currentPage} of {totalPages}"
            />
          </Col>
        </Row>
        </Container>
      </div>

      <GuestExploreOverlay
        visible={
          !isAuthenticated &&
          isShopLocked
        }
        clickCount={shopGuestStatus.clickCount}
        limit={shopGuestStatus.limit}
        verificationFailed={verificationFailed}
        daily={shopGuestStatus.daily}
        pageLabel={
          shopGuestStatus.reason === "total"
            ? "total"
            : "Shop"
        }
      />
    </>
  );
};

export default Shop;
